/**
 * DeviceAssignDrawer — Searchable device picker for assigning a device to the resident.
 *
 * Mirrors the Area Profile DeviceAssignDrawer pattern:
 *   - Only shows devices where is_area_device is false/0
 *   - Excludes devices already assigned to THIS resident
 *   - Sorts: unassigned first, then assigned-to-other-residents
 *   - Shows "Assigned to {resident_name}" indicator for devices on other residents
 *   - Requires confirmation before reassigning from another resident
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

/** Render device name as a link if manufacture_id is 1 or 4, else plain text */
const DeviceNameLink = ({ device }) => {
  const mid = device.manufacture_id;
  if (mid === 1 || mid === 4) {
    return (
      <Link to={`/community/devices/${mid}/${device.device_id}`} className="rpp-device-picker__link">
        {device.device_name || 'Unnamed Device'}
      </Link>
    );
  }
  return <>{device.device_name || 'Unnamed Device'}</>;
};

const DeviceAssignDrawer = ({
  open, onClose, onAssign, saving,
  availableDevices, resDeviceData, residentName,
}) => {
  const [search, setSearch] = useState('');
  const [confirmDevice, setConfirmDevice] = useState(null);

  // Reset state when drawer opens
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setSearch('');
      setConfirmDevice(null);
    }
    prevOpen.current = open;
  });

  /**
   * Filtered & sorted available devices:
   * 1. Only include non-area devices (is_area_device is false/0)
   * 2. Exclude devices already assigned to THIS resident
   * 3. Apply search filter
   * 4. Sort: unassigned first, then assigned-to-other-residents
   */
  const assignedIds = useMemo(() => {
    return new Set((resDeviceData || []).map((d) => d.device_id));
  }, [resDeviceData]);

  const filteredDevices = useMemo(() => {
    const term = search.toLowerCase().trim();

    return (availableDevices || [])
      .filter((d) => {
        // Only non-area devices (is_area_device can be 1/0 or true/false)
        if (Number(d.is_area_device)) return false;
        // Exclude devices already assigned to THIS resident
        if (assignedIds.has(d.device_id)) return false;
        // Search filter
        if (term) {
          return (
            (d.device_name || '').toLowerCase().includes(term) ||
            (d.manufacture_uuid || '').toLowerCase().includes(term) ||
            (d.descriptions || '').toLowerCase().includes(term) ||
            (d.model_name || '').toLowerCase().includes(term) ||
            (d.resident_name || '').toLowerCase().includes(term)
          );
        }
        return true;
      })
      .sort((a, b) => {
        // Unassigned first, then assigned-to-other-residents
        const aAssigned = (a.resident_id && Number(a.is_active)) ? 1 : 0;
        const bAssigned = (b.resident_id && Number(b.is_active)) ? 1 : 0;
        if (aAssigned !== bAssigned) return aAssigned - bAssigned;
        return (a.device_name || '').localeCompare(b.device_name || '');
      });
  }, [availableDevices, assignedIds, search]);

  /** Handle assign click — if device is on another resident, show confirmation */
  const handleAssign = useCallback((device) => {
    if (device.resident_id && Number(device.is_active)) {
      setConfirmDevice(device);
      return;
    }
    onAssign(device);
  }, [onAssign]);

  /** Confirm reassignment from another resident */
  const handleConfirmReassign = useCallback(() => {
    if (!confirmDevice) return;
    onAssign(confirmDevice);
    setConfirmDevice(null);
  }, [confirmDevice, onAssign]);

  /** Cancel reassignment */
  const handleCancelReassign = useCallback(() => {
    setConfirmDevice(null);
  }, []);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Assign Device</h2>
        <button className="dp-drawer__close" onClick={onClose}>✕</button>
      </div>

      {/* Sticky area: search + reassign confirmation */}
      <div className="dp-drawer-sticky">
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Search Devices</label>
          <input
            type="text"
            className="dp-drawer__input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name, serial, or description…"
            autoFocus
          />
        </div>

        {/* Reassign confirmation banner */}
        {confirmDevice ? (
          <div className="rpp-reassign-confirm">
            <div className="rpp-reassign-confirm__message">
              <svg className="rpp-reassign-confirm__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <div>
                <p className="rpp-reassign-confirm__title">Reassign device?</p>
                <p className="rpp-reassign-confirm__detail">
                  <strong>{confirmDevice.device_name}</strong> is currently assigned to{' '}
                  <strong>{confirmDevice.resident_name || 'another resident'}</strong>. Reassign to{' '}
                  <strong>{residentName}</strong>?
                </p>
              </div>
            </div>
            <div className="rpp-reassign-confirm__actions">
              <button
                type="button"
                className="dp-btn dp-btn--cancel dp-btn--sm"
                onClick={handleCancelReassign}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dp-btn dp-btn--warning dp-btn--sm"
                onClick={handleConfirmReassign}
                disabled={saving}
              >
                {saving ? 'Reassigning…' : 'Reassign'}
              </button>
            </div>
          </div>
        ) : null}

        <label className="dp-drawer__label">
          Available Devices ({filteredDevices.length})
        </label>
      </div>

      {/* Scrollable device list */}
      <div className="dp-drawer__body">
        <div className="rpp-device-picker">
          {filteredDevices.length === 0 ? (
            <div className="rpp-device-picker__empty">
              <p style={{ margin: 0 }}>
                {search.trim() ? 'No devices match your search.' : 'No unassigned resident devices available.'}
              </p>
              <p style={{ margin: 'var(--space-2) 0 0' }}>
                Can&apos;t find the device you&apos;re looking for?{' '}
                <Link to="/community/devices" state={{ openAddDevice: true }} style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-semibold)' }}>
                  Add a device
                </Link>
              </p>
            </div>
          ) : null}
          {filteredDevices.map((device) => {
            const isAssignedToOther = Boolean(device.resident_id && Number(device.is_active));
            return (
              <div
                key={device.device_id}
                className={`rpp-device-picker__item${isAssignedToOther ? ' rpp-device-picker__item--assigned' : ''}`}
              >
                <div className="rpp-device-picker__info">
                  <div className="rpp-device-picker__name">
                    <DeviceNameLink device={device} />
                  </div>
                  <div className="rpp-device-picker__meta">
                    {device.inovonics_model_name || device.model_name ? <span>{device.inovonics_model_name || device.model_name}</span> : null}
                    {(device.inovonics_model_name || device.model_name) && device.manufacture_uuid ? <span> · </span> : null}
                    {device.manufacture_uuid ? <span>{device.manufacture_uuid}</span> : <span>—</span>}
                  </div>
                  {device.descriptions ? (
                    <div className="rpp-device-picker__meta">{device.descriptions}</div>
                  ) : null}
                  {isAssignedToOther ? (
                    <div className="rpp-device-picker__assigned-to">
                      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                      </svg>
                      Assigned to {device.resident_name || 'another resident'}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={`rpp-device-picker__btn ${isAssignedToOther ? 'rpp-device-picker__btn--reassign' : 'rpp-device-picker__btn--assign'}`}
                  onClick={() => handleAssign(device)}
                  disabled={saving}
                  title={isAssignedToOther
                    ? `Reassign ${device.device_name} from ${device.resident_name || 'another resident'}`
                    : `Assign ${device.device_name}`
                  }
                >
                  {isAssignedToOther ? (
                    <>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                      </svg>
                      Reassign
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Assign
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="dp-drawer__footer">
        <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
          Close
        </button>
      </div>
    </div>
  );
};

export default DeviceAssignDrawer;
